import { useState } from 'react';
import { useAuth } from '../../../contexts/AuthContext';
import { supabase } from '../../../lib/supabase';

interface CouponModalProps {
  onClose: () => void;
  onSuccess: () => void;
}

export default function CouponModal({ onClose, onSuccess }: CouponModalProps) {
  const { user } = useAuth();
  const [couponCode, setCouponCode] = useState('');
  const [loading, setLoading] = useState(false);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!user || !couponCode.trim()) {
      alert('쿠폰 코드를 입력해주세요.');
      return;
    }

    setLoading(true);

    try {
      // 쿠폰 조회
      const { data: couponData, error: couponError } = await supabase
        .from('talent_coupons')
        .select('*')
        .eq('code', couponCode.trim().toUpperCase())
        .single();

      if (couponError || !couponData) {
        alert('유효하지 않은 쿠폰 코드입니다.');
        setLoading(false);
        return;
      }

      // 쿠폰 유효성 검사
      if (!couponData.is_active) {
        alert('사용이 중지된 쿠폰입니다.');
        setLoading(false);
        return;
      }

      if (new Date(couponData.expires_at) < new Date()) {
        alert('만료된 쿠폰입니다.');
        setLoading(false);
        return;
      }

      if (couponData.used_count >= couponData.max_uses) {
        alert('사용 횟수가 초과된 쿠폰입니다.');
        setLoading(false);
        return;
      }

      // 이미 사용한 쿠폰인지 확인
      const { data: usageData } = await supabase
        .from('coupon_usage')
        .select('*')
        .eq('coupon_id', couponData.id)
        .eq('user_id', user.id)
        .single();

      if (usageData) {
        alert('이미 사용한 쿠폰입니다.');
        setLoading(false);
        return;
      }

      // 달란트 적립
      const { data: pointData, error: pointError } = await supabase
        .from('talent_points')
        .select('balance, total_earned')
        .eq('user_id', user.id)
        .single();

      if (pointError) throw pointError;

      await supabase
        .from('talent_points')
        .update({
          balance: pointData.balance + couponData.amount,
          total_earned: pointData.total_earned + couponData.amount,
        })
        .eq('user_id', user.id);

      // 거래 내역 생성
      await supabase
        .from('point_transactions')
        .insert({
          user_id: user.id,
          amount: couponData.amount,
          type: 'reward',
          description: `쿠폰 사용: ${couponData.name}`,
        });

      // 쿠폰 사용 기록
      await supabase
        .from('coupon_usage')
        .insert({
          coupon_id: couponData.id,
          user_id: user.id,
        });

      // 쿠폰 사용 횟수 증가
      await supabase
        .from('talent_coupons')
        .update({
          used_count: couponData.used_count + 1,
        })
        .eq('id', couponData.id);

      alert(`${couponData.amount.toLocaleString()} 달란트가 적립되었습니다!`);
      onSuccess();
      onClose();
    } catch (error) {
      console.error('쿠폰 사용 실패:', error);
      alert('쿠폰 사용 중 오류가 발생했습니다.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-2xl max-w-md w-full p-6">
        <div className="flex items-center justify-between mb-6">
          <h3 className="text-2xl font-bold text-gray-900">쿠폰 등록</h3>
          <button
            onClick={onClose}
            className="w-8 h-8 flex items-center justify-center text-gray-400 hover:text-gray-600 cursor-pointer"
          >
            <i className="ri-close-line text-2xl"></i>
          </button>
        </div>

        <div className="mb-6">
          <div className="w-16 h-16 bg-teal-100 rounded-full flex items-center justify-center mx-auto mb-4">
            <i className="ri-coupon-3-line text-3xl text-teal-600"></i>
          </div>
          <p className="text-center text-gray-600">
            쿠폰 코드를 입력하면<br />달란트를 받을 수 있습니다
          </p>
        </div>

        <form onSubmit={handleSubmit} className="space-y-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              쿠폰 코드
            </label>
            <input
              type="text"
              value={couponCode}
              onChange={(e) => setCouponCode(e.target.value.toUpperCase())}
              placeholder="쿠폰 코드 입력"
              className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-teal-500 focus:border-transparent text-center text-lg font-mono tracking-wider"
              required
            />
          </div>

          <div className="flex gap-3 pt-4">
            <button
              type="button"
              onClick={onClose}
              className="flex-1 px-6 py-3 border border-gray-300 text-gray-700 font-semibold rounded-lg hover:bg-gray-50 transition-colors cursor-pointer whitespace-nowrap"
            >
              취소
            </button>
            <button
              type="submit"
              disabled={loading}
              className="flex-1 px-6 py-3 bg-teal-600 text-white font-semibold rounded-lg hover:bg-teal-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed cursor-pointer whitespace-nowrap"
            >
              {loading ? '확인 중...' : '등록하기'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}
